import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext.js";
import "./styles/Profile.css";

const Settings = () => {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteSection, setShowDeleteSection] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const resp = await fetch("/api/user/me", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setEmail(data.email || "");
          setDisplayName(data.display_name || data.username || "");
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
      } finally {
        setProfileLoading(false);
      }
    };
    if (token) fetchProfile();
  }, [token]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const resp = await fetch("/api/user/me", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email, display_name: displayName })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      setMessage("Profile updated successfully.");
      setMessageType("success");
    } catch (err) {
      setMessage(err.message);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setMessage("New password must be at least 6 characters.");
      setMessageType("error");
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage("New passwords do not match.");
      setMessageType("error");
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const resp = await fetch("/api/user/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      setMessage("Password changed successfully.");
      setMessageType("success");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setMessage(err.message);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (deleteConfirmText !== "DELETE") {
      setMessage("Please type DELETE to confirm.");
      setMessageType("error");
      return;
    }
    if (!deletePassword) {
      setMessage("Please enter your password to confirm deletion.");
      setMessageType("error");
      return;
    }
    setDeleteLoading(true);
    setMessage(null);
    try {
      const resp = await fetch("/api/user/me", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ password: deletePassword })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      logout();
      navigate("/", { replace: true });
    } catch (err) {
      setMessage(err.message);
      setMessageType("error");
      setDeleteLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <p className="profile-loading">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <h1>Settings</h1>

        {message && (
          <div className={`settings-message settings-message-${messageType}`}>
            {message}
          </div>
        )}

        <div className="profile-section">
          <h2>Account Information</h2>
          <form onSubmit={handleUpdateProfile} className="settings-form">
            <div className="settings-field">
              <label className="settings-label">Username</label>
              <input type="text" value={user.username} disabled className="settings-input settings-input-disabled" />
              <span className="settings-hint">Username cannot be changed</span>
            </div>
            <div className="settings-field">
              <label className="settings-label">Display Name</label>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="settings-input" placeholder="Your display name" />
            </div>
            <div className="settings-field">
              <label className="settings-label">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="settings-input" placeholder="your@email.com" />
            </div>
            <button type="submit" disabled={loading} className="settings-save-btn">
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>

        <div className="profile-section">
          <h2>Change Password</h2>
          <form onSubmit={handleChangePassword} className="settings-form">
            <div className="settings-field">
              <label className="settings-label">Current Password</label>
              <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="settings-input" required />
            </div>
            <div className="settings-field">
              <label className="settings-label">New Password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="settings-input" placeholder="At least 6 characters" required />
            </div>
            <div className="settings-field">
              <label className="settings-label">Confirm New Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="settings-input" required />
            </div>
            <button type="submit" disabled={loading} className="settings-save-btn">
              {loading ? "Changing..." : "Change Password"}
            </button>
          </form>
        </div>

        <div className="profile-section delete-account-section">
          <h2>Delete Account</h2>
          <p className="delete-account-warning">
            Permanently deletes your account and all associated data including your subscription history and article access records. <strong>This cannot be undone.</strong>
          </p>
          {!showDeleteSection ? (
            <div className="settings-form delete-trigger-wrap">
              <button className="delete-account-trigger-btn" onClick={() => setShowDeleteSection(true)}>
                Delete My Account
              </button>
            </div>
          ) : (
            <form onSubmit={handleDeleteAccount} className="settings-form">
              <div className="settings-field">
                <label className="settings-label">Type <strong>DELETE</strong> to confirm</label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="settings-input"
                  placeholder="DELETE"
                  autoComplete="off"
                />
              </div>
              <div className="settings-field">
                <label className="settings-label">Enter your password</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="settings-input"
                  placeholder="Your current password"
                  required
                />
              </div>
              <div className="delete-account-actions">
                <button
                  type="submit"
                  disabled={deleteLoading || deleteConfirmText !== "DELETE"}
                  className="delete-account-confirm-btn"
                >
                  {deleteLoading ? "Deleting..." : "Permanently Delete Account"}
                </button>
                <button
                  type="button"
                  className="settings-save-btn"
                  onClick={() => { setShowDeleteSection(false); setDeletePassword(""); setDeleteConfirmText(""); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
